import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";

import { AdvertisementType } from "@/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import UploadInput from "@/features/file-upload/components/upload-input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useCreateAdvertisement } from "../api/create-advertisement";
import { advertisementsQueryKey } from "../api/get-advertisements";
import {
  type CreateAdvertisementFormData,
  createAdvertisementSchema,
} from "../types/create-advertisement-schema";

interface CreateAdvertisementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CreateAdvertisementDialog({
  open,
  onOpenChange,
}: CreateAdvertisementDialogProps) {
  const { t } = useTranslation();
  const { congress } = useCongress();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getDefaultType = (): AdvertisementType => {
    if (location.pathname.includes("/splash")) {
      return AdvertisementType.SPLASH_SCREEN;
    }
    return AdvertisementType.SPLASH_SCREEN;
  };

  const form = useForm<CreateAdvertisementFormData>({
    resolver: zodResolver(createAdvertisementSchema),
    defaultValues: {
      name: "",
      type: getDefaultType(),
      image: {
        id: "",
        url: "",
      },
    },
  });

  const { mutate: createAdvertisement } = useCreateAdvertisement({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: advertisementsQueryKey(congress?.id),
      });
      onOpenChange(false);
      form.reset();
      setIsSubmitting(false);
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: CreateAdvertisementFormData) => {
    if (!congress?.id) return;

    setIsSubmitting(true);
    createAdvertisement({
      requestBody: {
        name: data.name,
        type: data.type,
        imageId: data.image.id,
        congressId: congress.id,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("ads.createTitle")}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ads.name")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("ads.namePlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select advertisement type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="SPLASH_SCREEN">
                        Splash Screen
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            /> */}
            <FormField
              control={form.control}
              name="image"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ads.image")}</FormLabel>
                  <FormControl>
                    <UploadInput
                      isSubmitting={form.formState.isSubmitting}
                      onChange={field.onChange}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("ads.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t("ads.creating") : t("ads.create")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export { CreateAdvertisementDialog };
